import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { vendorObsApi } from '@/services/vendor.api';
import { StatCard } from '@/components/vendor/StatCard';
import { MiniDonut } from '@/components/vendor/charts/MiniDonut';
import { TimeSeries } from '@/components/vendor/charts/TimeSeries';
import { BarBreakdown } from '@/components/vendor/charts/BarBreakdown';

const WINDOWS = [
  { key: '24h', label: '24h', windowHours: 24, bucket: 'hour' as const },
  { key: '7d', label: '7d', windowHours: 168, bucket: 'day' as const },
  { key: '30d', label: '30d', windowHours: 720, bucket: 'day' as const },
];

const SERIES_COLORS = {
  emailSent: '#38bdf8',
  emailFailed: '#f87171',
  syncSuccess: '#34d399',
  syncFailed: '#fbbf24',
};

/** A dark panel wrapper with a title and per-panel loading/empty/error slots. */
function Panel({
  title,
  right,
  children,
  className = '',
}: {
  title: string;
  right?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={`min-w-0 rounded-lg border border-slate-800 bg-slate-900 p-4 ${className}`}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-sm font-medium text-slate-300">{title}</h2>
        {right}
      </div>
      {children}
    </section>
  );
}

/** Vendor observability Overview — the "find anomalies" landing console. */
export function VendorHealthPage() {
  const [win, setWin] = useState(WINDOWS[0]);

  const health = useQuery({
    queryKey: ['vendor', 'health'],
    queryFn: vendorObsApi.health,
    refetchInterval: 30_000,
  });

  const metrics = useQuery({
    queryKey: ['vendor', 'metrics', win.windowHours, win.bucket],
    queryFn: () =>
      vendorObsApi.metrics({ windowHours: win.windowHours, bucket: win.bucket }),
    refetchInterval: 60_000,
  });

  const formatBucket = useMemo(
    () =>
      (iso: string): string => {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return iso;
        return win.bucket === 'hour'
          ? d.toLocaleTimeString(undefined, { hour: 'numeric' })
          : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      },
    [win.bucket],
  );

  const h = health.data;
  const m = metrics.data;

  const refreshAll = () => {
    health.refetch();
    metrics.refetch();
  };

  const busy = health.isFetching || metrics.isFetching;

  return (
    <div className="min-w-0 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold text-white">Overview</h1>
          <p className="text-xs text-slate-500">
            Email &amp; channel-sync health across BestAcco properties.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex overflow-hidden rounded-md border border-slate-700">
            {WINDOWS.map((w) => (
              <button
                key={w.key}
                onClick={() => setWin(w)}
                className={`px-3 py-1.5 text-sm transition ${
                  win.key === w.key
                    ? 'bg-amber-500 font-semibold text-slate-950'
                    : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                }`}
              >
                {w.label}
              </button>
            ))}
          </div>
          <button
            onClick={refreshAll}
            className="rounded-md bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700"
          >
            {busy ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* ── Metric cards ─────────────────────────────────────────── */}
      {health.isLoading && <div className="text-slate-400">Loading health…</div>}
      {health.isError && (
        <div className="rounded-md bg-red-500/10 px-3 py-2 text-sm text-red-300">
          Could not load health snapshot.
        </div>
      )}
      {h && (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatCard
              label={`Emails sent (${h.windowHours}h)`}
              value={h.email.sent.toLocaleString()}
              tone="default"
              sub={`${h.email.total.toLocaleString()} attempted`}
            />
            <StatCard
              label="Emails failed"
              value={h.email.failed.toLocaleString()}
              tone={h.email.failed > 0 ? 'bad' : 'good'}
              sub={`${h.email.failRatePct}% fail rate`}
              accessory={
                <MiniDonut
                  pct={h.email.failRatePct}
                  color={h.email.failed > 0 ? '#f87171' : '#34d399'}
                  label={`${h.email.failRatePct}% email fail rate`}
                />
              }
            />
            <StatCard
              label="Sync errors"
              value={h.channelSync.errors.toLocaleString()}
              tone={h.channelSync.errors > 0 ? 'bad' : 'good'}
              sub={`${h.channelSync.errorRatePct}% of ${h.channelSync.total.toLocaleString()}`}
              accessory={
                <MiniDonut
                  pct={h.channelSync.errorRatePct}
                  color={h.channelSync.errors > 0 ? '#f87171' : '#34d399'}
                  label={`${h.channelSync.errorRatePct}% sync error rate`}
                />
              }
            />
            <StatCard
              label="Database"
              value={h.db.ok ? 'OK' : 'DOWN'}
              tone={h.db.ok ? 'good' : 'bad'}
              sub={h.db.ok ? 'responding' : 'not responding'}
            />
          </div>

          {/* migration is a footnote, never a headline — truncated, tooltipped */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-600">
            <span className="min-w-0 max-w-full truncate" title={h.migrations.latest ?? '—'}>
              Latest migration: {h.migrations.latest ?? '—'}
            </span>
            <span className="shrink-0">
              Generated {new Date(h.generatedAt).toLocaleString()}
            </span>
          </div>
        </>
      )}

      {/* ── Volume time-series ───────────────────────────────────── */}
      <Panel title={`Volume over time · ${win.label}`}>
        {metrics.isLoading && <div className="text-slate-400">Loading metrics…</div>}
        {metrics.isError && (
          <div className="rounded-md bg-red-500/10 px-3 py-2 text-sm text-red-300">
            Could not load metrics.
          </div>
        )}
        {m && m.buckets.length === 0 && (
          <div className="py-8 text-center text-sm text-slate-500">
            No activity in this window.
          </div>
        )}
        {m && m.buckets.length > 0 && (
          <TimeSeries
            buckets={m.buckets}
            formatBucket={formatBucket}
            series={[
              { key: 'emailSent', label: 'Email sent', color: SERIES_COLORS.emailSent, values: m.series.emailSent },
              { key: 'emailFailed', label: 'Email failed', color: SERIES_COLORS.emailFailed, values: m.series.emailFailed },
              { key: 'syncSuccess', label: 'Sync success', color: SERIES_COLORS.syncSuccess, values: m.series.syncSuccess },
              { key: 'syncFailed', label: 'Sync failed', color: SERIES_COLORS.syncFailed, values: m.series.syncFailed },
            ]}
          />
        )}
      </Panel>

      {/* ── Breakdowns ───────────────────────────────────────────── */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Email by template">
          {metrics.isLoading && <div className="text-slate-400">Loading…</div>}
          {metrics.isError && (
            <div className="text-sm text-red-300">Could not load.</div>
          )}
          {m && (
            <BarBreakdown
              color="#38bdf8"
              items={m.emailByTemplate.map((t) => ({
                key: t.template,
                label: t.template,
                count: t.count,
                failed: t.failed,
              }))}
            />
          )}
        </Panel>

        <Panel title="Sync by channel">
          {metrics.isLoading && <div className="text-slate-400">Loading…</div>}
          {metrics.isError && (
            <div className="text-sm text-red-300">Could not load.</div>
          )}
          {m && (
            <BarBreakdown
              color="#34d399"
              items={m.syncByChannel.map((c) => ({
                key: c.channel,
                label: c.channel,
                count: c.count,
                failed: c.failed,
              }))}
            />
          )}
        </Panel>
      </div>

      {/* ── Top failing hotels ───────────────────────────────────── */}
      <Panel title="Top failing hotels">
        {metrics.isLoading && <div className="text-slate-400">Loading…</div>}
        {metrics.isError && (
          <div className="text-sm text-red-300">Could not load.</div>
        )}
        {m && m.topFailingHotels.length === 0 && (
          <div className="py-6 text-center text-sm text-slate-500">
            No failures in this window. 🎉
          </div>
        )}
        {m && m.topFailingHotels.length > 0 && (
          <ol className="space-y-1">
            {m.topFailingHotels.map((hotel, i) => (
              <li key={hotel.hotelId}>
                <Link
                  to={`/vendor/activity?propertyId=${encodeURIComponent(hotel.hotelId)}&status=failed`}
                  className="flex items-center gap-3 rounded-md px-2 py-2 hover:bg-slate-800/70"
                >
                  <span className="w-5 shrink-0 text-right text-xs tabular-nums text-slate-500">
                    {i + 1}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm text-slate-200" title={hotel.hotel}>
                    {hotel.hotel}
                  </span>
                  <span className="shrink-0 rounded-full bg-red-500/10 px-2 py-0.5 text-xs font-medium text-red-300">
                    {hotel.failures.toLocaleString()} failed
                  </span>
                </Link>
              </li>
            ))}
          </ol>
        )}
      </Panel>
    </div>
  );
}
